import { useState, useEffect, useCallback } from 'react';
import { css } from '@emotion/react';
import {
  PhoneHoursExpansion,
  CallObject,
  Node,
  PhoneHoursScheduleRule,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { isEqual } from 'lodash-es';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { PhoneHours } from '@frontend/phones#svg';
import { theme } from '@frontend/theme';
import { Accordion, Heading, Text, TextLink, useModalControl } from '@frontend/design-system';
import { CalendarToggle } from '../../phone-hours/calendar-toggle';
import CalendarView from '../../phone-hours/calendar-view';
import { DayHoursList } from '../../phone-hours/day-hours-list';
import { PhoneHoursStepForm } from '../../phone-hours/phone-hours-step-form';
import { SwitchToLargeModal } from '../../phone-hours/switch-to-large-modal';
import { TimezoneDisplay } from '../../phone-hours/timezone-display';
import { PhoneHoursNodeState } from './add-step-panel/types';

export type PhoneHoursCallObject = Extract<CallObject, { phoneHoursExpansion: PhoneHoursExpansion }>;
export type OfficeHoursObject = Extract<Node, { callObject: PhoneHoursCallObject }>;

export function isPhoneHoursCallObject(callObject: CallObject): callObject is PhoneHoursCallObject {
  return 'phoneHoursExpansion' in callObject;
}

type PhoneHoursPanelProps = {
  officeHourNodes: Node[];
  onSavePhoneHours: (params: { nodeId: string; nodeUpdate: PhoneHoursNodeState }) => void;
  hoursManagementPermission: boolean;
  onCalendarViewChange?: (isOpen: boolean) => void;
  onEditModeChange?: (isEdit: boolean) => void;
  triggerPhoneHoursEdit?: boolean;
  isCalendarViewOpen?: boolean;
  processingNodeId?: string;
  isSmallMode?: boolean;
};

export const PhoneHoursPanel = ({
  officeHourNodes,
  onSavePhoneHours,
  hoursManagementPermission,
  onCalendarViewChange,
  onEditModeChange,
  triggerPhoneHoursEdit,
  isCalendarViewOpen,
  processingNodeId,
  isSmallMode = false,
}: PhoneHoursPanelProps) => {
  const { t } = useTranslation('phone');
  const [showCalendarView, setShowCalendarView] = useState(isCalendarViewOpen ?? false);
  const [isEditMode, setIsEditMode] = useState(triggerPhoneHoursEdit || false);
  // Indicates whether current form state is valid/dirty enough to save.
  const [canSaveForm, setCanSaveForm] = useState(false);
  // Points to the phone hours node currently being edited.
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  // Holds unsaved phone-hours edits so calendar preview updates live during editing.
  const [liveEditingPhoneHours, setLiveEditingPhoneHours] = useState<PhoneHoursScheduleRule[] | null>(null);
  // Stores the Phone Hours data from when editing starts, so the form stays on the same item while edits are in progress.
  const [editingCallObjectSnapshot, setEditingCallObjectSnapshot] = useState<PhoneHoursCallObject | null>(null);
  // Increments to request a save from the child PhoneHoursStepForm.
  const [saveRequestId, setSaveRequestId] = useState(0);

  // Automatically enter edit mode when triggered from outside
  useEffect(() => {
    if (!triggerPhoneHoursEdit) return;
    if (!processingNodeId) return;
    setIsEditMode(true);

    if (officeHourNodes.length > 1) {
      setEditingNodeId(processingNodeId);
    } else {
      setEditingNodeId(officeHourNodes[0]?.id || null);
    }
    setEditingCallObjectSnapshot(null);
    setCanSaveForm(false);
    onEditModeChange?.(true);
  }, [triggerPhoneHoursEdit, processingNodeId, officeHourNodes, onEditModeChange]);

  // Ensures parent edit-mode state is reset when this panel unmounts.
  useEffect(() => {
    return () => {
      onEditModeChange?.(false);
    };
  }, [onEditModeChange]);

  // Sync calendar view state with prop changes
  useEffect(() => {
    if (isCalendarViewOpen !== undefined) {
      setShowCalendarView(isCalendarViewOpen);
    }
  }, [isCalendarViewOpen]);

  const phoneHourCallObjects: PhoneHoursCallObject[] = officeHourNodes
    .map((item) => item.callObject)
    .filter(isPhoneHoursCallObject);

  // Modal for unsupported calendar view in small mode
  const unsupportedCalendarModal = useModalControl();

  // Toggles calendar visibility and keeps the parent calendar state in sync.
  const handleShowCalendarClick = () => {
    // In small mode, show unsupported modal instead of opening calendar
    if (isSmallMode) {
      unsupportedCalendarModal.openModal();
      return;
    }
    const newValue = !showCalendarView;
    setShowCalendarView(newValue);
    onCalendarViewChange?.(newValue);
  };

  // Enters edit mode for single-node scenarios.
  const handleEditClick = () => {
    setLiveEditingPhoneHours(null);
    setEditingCallObjectSnapshot(null);
    setIsEditMode(true);
    onEditModeChange?.(true);
  };

  // Enters edit mode and targets a specific node from the accordion list.
  const handleAccordionEditClick = (nodeId: string) => {
    setLiveEditingPhoneHours(null);
    setEditingCallObjectSnapshot(null);
    setIsEditMode(true);
    setEditingNodeId(nodeId);
    onEditModeChange?.(true);
  };

  // Persists form output to the selected node, then exits edit mode.
  const handleSaveFromForm = (nodeUpdate: PhoneHoursNodeState) => {
    // Route Phone Hours form edits through the dedicated save handler.
    const nodeIdToSave = editingNodeId || officeHourNodes[0]?.id;
    if (nodeIdToSave) {
      onSavePhoneHours({ nodeId: nodeIdToSave, nodeUpdate });
    }
    setIsEditMode(false);
    setCanSaveForm(false);
    setEditingCallObjectSnapshot(null);
    onEditModeChange?.(false);
  };

  // Requests the mounted PhoneHoursStepForm to run its save flow.
  const handleSaveClick = () => {
    setSaveRequestId((prev) => prev + 1);
  };

  // Cancels editing and resets transient editing state.
  const handleCancelClick = () => {
    // Reset form by exiting edit mode
    setIsEditMode(false);
    setCanSaveForm(false);
    setEditingCallObjectSnapshot(null);
    onEditModeChange?.(false);
  };

  // Receives save eligibility updates from the nested step form.
  const handleCanSaveChange = (canSave: boolean) => {
    setCanSaveForm(canSave);
  };

  // Resolves the active editing node, with a safe fallback to the first node.
  const getEditingNode = useCallback(() => {
    if (!editingNodeId) return officeHourNodes[0];
    return officeHourNodes.find((node) => node.id === editingNodeId) || officeHourNodes[0];
  }, [editingNodeId, officeHourNodes]);

  // Resolves the call object used to seed and render current edit state.
  const getEditingCallObject = useCallback(() => {
    const editingNode = getEditingNode();
    return editingNode?.callObject && isPhoneHoursCallObject(editingNode.callObject)
      ? editingNode.callObject
      : phoneHourCallObjects[0];
  }, [getEditingNode, phoneHourCallObjects]);

  // Captures a stable call-object snapshot the first time edit mode starts.
  useEffect(() => {
    if (!isEditMode) {
      setEditingCallObjectSnapshot(null);
      return;
    }

    if (editingCallObjectSnapshot) {
      return;
    }

    setEditingCallObjectSnapshot(getEditingCallObject());
  }, [isEditMode, editingCallObjectSnapshot, getEditingCallObject]);

  const editingCallObject = editingCallObjectSnapshot ?? getEditingCallObject();

  // Initializes the live preview model once per edit session.
  useEffect(() => {
    if (!isEditMode) {
      setLiveEditingPhoneHours(null);
      return;
    }

    if (liveEditingPhoneHours !== null) {
      return;
    }

    setLiveEditingPhoneHours(getEditingCallObject()?.phoneHoursExpansion?.phoneHours ?? []);
  }, [isEditMode, liveEditingPhoneHours, getEditingCallObject]);

  // Updates calendar preview state as the form changes while avoiding redundant updates.
  const handlePreviewPhoneHoursChange = (phoneHours: PhoneHoursScheduleRule[]) => {
    setLiveEditingPhoneHours((prev) => (isEqual(prev, phoneHours) ? prev : phoneHours));
  };

  const singleNodePhoneHours = phoneHourCallObjects[0]?.phoneHoursExpansion?.phoneHours ?? [];
  const singleNodeCalendarPhoneHours = isEditMode
    ? liveEditingPhoneHours ?? singleNodePhoneHours
    : singleNodePhoneHours;

  const multiNodeCalendarPhoneHours = isEditMode
    ? liveEditingPhoneHours ?? editingCallObject?.phoneHoursExpansion?.phoneHours ?? []
    : editingCallObject?.phoneHoursExpansion?.phoneHours ?? [];

  return (
    <>
      {phoneHourCallObjects.length === 0 && (
        <div
          css={css`
            width: 100%;
            display: flex;
            justify-content: center;
          `}
        >
          <div
            css={css`
              width: 336px;
            `}
          >
            <PhoneHours />
            <Text textAlign='center' color='subdued'>
              {t('No Phone Hours.')}
            </Text>
            <Text textAlign='center' color='subdued'>
              {t('Add a Phone Hours step in your Call Route to route calls differently by time of day.')}
            </Text>
          </div>
        </div>
      )}
      {phoneHourCallObjects.length === 1 && (
        <div
          css={{
            display: 'flex',
            flexDirection: showCalendarView ? 'row' : 'column',
            alignItems: 'flex-start',
            width: '100%',
          }}
        >
          {showCalendarView && !isSmallMode && (
            <div
              css={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                maxWidth: isEditMode ? 'calc(100% - 624px - 24px)' : 'calc(100% - 360px - 24px)',
                padding: '2px',
              }}
            >
              {!isSmallMode && (
                <div css={{ marginBottom: theme.spacing(5), flexShrink: 0 }}>
                  <CalendarToggle
                    showCalendarView={showCalendarView}
                    onToggle={handleShowCalendarClick}
                    hasPermission={hoursManagementPermission}
                    isSmallMode={isSmallMode}
                  />
                </div>
              )}
              {!isSmallMode && (
                <div>
                  <CalendarView phoneHours={singleNodeCalendarPhoneHours} />
                </div>
              )}
            </div>
          )}
          <div
            css={{
              flex: showCalendarView && !isSmallMode ? (isEditMode ? '0 0 624px' : '0 0 360px') : 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(1),
              width: showCalendarView && !isSmallMode ? (isEditMode ? '624px' : '360px') : '100%',
              minWidth: showCalendarView && !isSmallMode ? (isEditMode ? '624px' : '360px') : 'auto',
              marginLeft: showCalendarView ? 'auto' : 'auto',
            }}
          >
            <div
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
                {(!showCalendarView || (showCalendarView && isSmallMode)) && (
                  <CalendarToggle
                    showCalendarView={showCalendarView}
                    onToggle={handleShowCalendarClick}
                    hasPermission={hoursManagementPermission}
                    isSmallMode={isSmallMode}
                  />
                )}
              </div>
              {isEditMode ? (
                <div css={{ display: 'flex', gap: theme.spacing(2) }}>
                  <TextLink
                    onClick={canSaveForm ? handleSaveClick : handleCancelClick}
                    css={{ display: 'flex', gap: theme.spacing(0.5), width: 'auto', alignItems: 'center' }}
                    weight='bold'
                    size='large'
                  >
                    {canSaveForm ? t('Done') : t('View Hours')}
                  </TextLink>
                </div>
              ) : (
                <TextLink
                  disabled={!hoursManagementPermission}
                  onClick={handleEditClick}
                  css={{ display: 'flex', gap: theme.spacing(0.5), width: 'auto', alignItems: 'center' }}
                  weight='bold'
                  size='large'
                >
                  {t('Edit Hours')}
                </TextLink>
              )}
            </div>
            <div css={{ marginBottom: theme.spacing(1), alignSelf: 'flex-end' }}>
              <TimezoneDisplay timezone={editingCallObject?.phoneHoursExpansion?.timezone} />
            </div>
            {isEditMode ? (
              <PhoneHoursStepForm
                key={`edit-${officeHourNodes[0]?.id}`} // Force remount when nodeId changes
                initialState={{
                  callObject: {
                    primitiveId: editingCallObject?.primitiveId ?? '',
                    primitiveName: editingCallObject?.primitiveName ?? '',
                    instructionId: editingCallObject?.instructionId ?? '',
                    instructionSetId: editingCallObject?.instructionSetId ?? '',
                    phoneHoursExpansion: {
                      phoneHours: editingCallObject?.phoneHoursExpansion?.phoneHours ?? [],
                    },
                  },
                }}
                onSave={handleSaveFromForm}
                onCancel={handleCancelClick}
                onCanSaveChange={handleCanSaveChange}
                onPhoneHoursChange={handlePreviewPhoneHoursChange}
                saveRequestId={saveRequestId}
                compact={false}
                isSmallMode={isSmallMode}
              />
            ) : (
              <DayHoursList
                useLargerFonts
                phoneHours={phoneHourCallObjects[0].phoneHoursExpansion.phoneHours}
                isSmallMode={isSmallMode}
              />
            )}
          </div>
        </div>
      )}
      {officeHourNodes.length > 1 && (
        <>
          {isEditMode && editingNodeId ? (
            <div
              css={css`
                display: flex;
                flex-direction: ${showCalendarView ? 'row' : 'column'};
                gap: ${showCalendarView ? theme.spacing(3) : theme.spacing(2)};
                align-items: ${showCalendarView ? 'flex-start' : 'flex-start'};
                width: 100%;
              `}
            >
              {showCalendarView && !isSmallMode && (
                <div
                  css={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: isEditMode ? 'calc(100% - 624px - 24px)' : 'calc(100% - 360px - 24px)',
                    padding: '2px',
                  }}
                >
                  <div css={{ marginBottom: theme.spacing(5), flexShrink: 0 }}>
                    <CalendarToggle
                      showCalendarView={showCalendarView}
                      onToggle={handleShowCalendarClick}
                      hasPermission={hoursManagementPermission}
                      isSmallMode={isSmallMode}
                    />
                  </div>
                  {!isSmallMode && (
                    <div>
                      <CalendarView phoneHours={multiNodeCalendarPhoneHours} />
                    </div>
                  )}
                </div>
              )}
              <div
                css={{
                  flex: showCalendarView && !isSmallMode ? (isEditMode ? '0 0 624px' : '0 0 360px') : 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.spacing(1),
                  width: showCalendarView && !isSmallMode ? (isEditMode ? '624px' : '360px') : '100%',
                  minWidth: showCalendarView && !isSmallMode ? (isEditMode ? '624px' : '360px') : 'auto',
                  marginLeft: showCalendarView ? 'auto' : 'auto',
                }}
              >
                <div
                  css={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
                    {(!showCalendarView || isSmallMode) && (
                      <CalendarToggle
                        showCalendarView={showCalendarView}
                        onToggle={handleShowCalendarClick}
                        hasPermission={hoursManagementPermission}
                        isSmallMode={isSmallMode}
                      />
                    )}
                  </div>
                  <div css={{ display: 'flex', gap: theme.spacing(2) }}>
                    <TextLink
                      onClick={canSaveForm ? handleSaveClick : handleCancelClick}
                      css={{ display: 'flex', gap: theme.spacing(0.5), width: 'auto', alignItems: 'center' }}
                      weight='bold'
                      size='large'
                    >
                      {canSaveForm ? t('Done') : t('View Hours')}
                    </TextLink>
                  </div>
                </div>
                <div css={{ marginBottom: theme.spacing(1), alignSelf: 'flex-end' }}>
                  <TimezoneDisplay timezone={editingCallObject?.phoneHoursExpansion?.timezone} />
                </div>
                <div
                  css={{
                    display: 'flex',
                    justifyContent: 'center',
                    overflowX: 'auto',
                    overflowY: 'visible',
                    width: '100%',
                  }}
                >
                  <div
                    css={{
                      minWidth: 0,
                      maxWidth: '100%',
                    }}
                  >
                    <PhoneHoursStepForm
                      key={`edit-accordion-${editingNodeId}`}
                      initialState={{
                        callObject: {
                          primitiveId: editingCallObject?.primitiveId ?? '',
                          primitiveName: editingCallObject?.primitiveName ?? '',
                          instructionId: editingCallObject?.instructionId ?? '',
                          instructionSetId: editingCallObject?.instructionSetId ?? '',
                          phoneHoursExpansion: {
                            phoneHours: editingCallObject?.phoneHoursExpansion?.phoneHours ?? [],
                          },
                        },
                      }}
                      onSave={handleSaveFromForm}
                      onCancel={handleCancelClick}
                      onCanSaveChange={handleCanSaveChange}
                      onPhoneHoursChange={handlePreviewPhoneHoursChange}
                      saveRequestId={saveRequestId}
                      compact={false}
                      isSmallMode={isSmallMode}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Show accordion view
            officeHourNodes
              .filter((item) => isPhoneHoursCallObject(item.callObject))
              .map((item) => {
                const callObject = item.callObject as PhoneHoursCallObject;
                const { phoneHoursExpansion } = callObject;
                return (
                  <Accordion
                    key={item.id}
                    css={css`
                      > div {
                        box-shadow: none;
                        border: 1px solid ${theme.colors.neutral20};
                      }

                      > div h3 button section div svg {
                        fill: ${theme.colors.neutral70};
                      }

                      div[data-active='true'] h3 button {
                        background-color: ${theme.colors.neutral5};
                        border-bottom: 1px solid ${theme.colors.neutral10};
                      }
                    `}
                  >
                    <Accordion.Item value={'phoneHours'}>
                      <Accordion.Header>
                        <div
                          css={css`
                            display: flex;
                            align-items: center;
                            gap: ${theme.spacing(2)};
                          `}
                        >
                          <Icon name='clock' />
                          <Heading level={3}>{callObject.primitiveName}</Heading>
                          {hoursManagementPermission && (
                            <Icon
                              name='edit'
                              onClick={() => {
                                handleAccordionEditClick(item.id);
                              }}
                              css={{
                                position: 'absolute',
                                right: theme.spacing(7),
                                color: theme.colors.neutral90,
                                cursor: 'pointer',
                              }}
                            />
                          )}
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        <DayHoursList
                          useLargerFonts
                          phoneHours={phoneHoursExpansion.phoneHours}
                          isSmallMode={isSmallMode}
                        />
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                );
              })
          )}
        </>
      )}
      <SwitchToLargeModal {...unsupportedCalendarModal.modalProps} />
    </>
  );
};
